import { formatDateRu, formatKg, PHOTO_KIND_NAMES, type PhotoKind } from '@pms/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuthStore } from '../../auth/store.js';
import { request } from '../../sync/api.js';
import { syncNow } from '../../sync/engine.js';
import { enqueue } from '../../sync/outbox.js';

/**
 * Приёмка смен мастером.
 *
 * Оператор сдаёт цифры, мастер их подтверждает — и только тогда смена считается
 * закрытой. Смысл шага в том, чтобы ошибка нашлась в тот же день, а не через
 * месяц при разборе расхождений на складе.
 *
 * Приёмка уходит событием в общую очередь: мастер ходит по цеху с телефоном,
 * и ждать связи, стоя у агрегата, ему незачем.
 */

interface ReportRow {
  id: string;
  date: string;
  shiftNumber: number;
  site: { id: string; name: string };
  machine: { id: string; name: string };
  participants: Array<{ user: { id: string; fullName: string }; shareBp: number }>;
  status: 'REPORTED' | 'ACCEPTED' | 'CORRECTED' | 'SUPERSEDED';
  photoStatus: 'NONE' | 'PENDING' | 'UPLOADED';
  acceptedAt: string | null;
  comment: string | null;
  batchesCount: number | null;
  outputs: Array<{ productSize: { id: string; name: string }; qtyPieces: number; defectPieces: number }>;
  materials: Array<{ materialType: { id: string; name: string }; direction: string; qtyG: string }>;
  crewWork: { palletsPacked: number; palletsLoaded: number; tasks: string[] } | null;
  photos: Array<{ id: string; kind: PhotoKind }>;
}

export function AcceptanceScreen(): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const [reports, setReports] = useState<ReportRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const response = await request<{ reports: ReportRow[] }>(
        '/api/reports?limit=100',
        { method: 'GET' },
        20_000,
      );
      setReports(response.reports);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить смены');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const accept = async (report: ReportRow): Promise<void> => {
    if (!user) return;
    const comment = window.prompt(
      `Принять смену: ${report.machine.name}, ${formatDateRu(report.date)}, смена ${report.shiftNumber}?\n\nЗамечание (необязательно):`,
      '',
    );
    if (comment === null) return;

    setBusy(report.id);
    setError(null);
    setNotice(null);
    try {
      await enqueue({
        type: 'SHIFT_REPORT_ACCEPTED',
        aggregateType: 'SHIFT_REPORT',
        aggregateId: report.id,
        userId: user.id,
        payload: { reportId: report.id, comment: comment.trim() || null },
      });
      await syncNow();
      await load();
      setNotice('Смена принята');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не получилось принять');
    } finally {
      setBusy(null);
    }
  };

  const pending = (reports ?? []).filter((report) => report.status === 'REPORTED');
  const accepted = (reports ?? []).filter((report) => report.status === 'ACCEPTED');

  const summary = (report: ReportRow): string => {
    const parts: string[] = [];
    for (const output of report.outputs) {
      parts.push(
        `${output.productSize.name}: ${output.qtyPieces} шт` +
          (output.defectPieces > 0 ? `, брак ${output.defectPieces}` : ''),
      );
    }
    if (report.batchesCount !== null) parts.push(`замесов ${report.batchesCount}`);
    for (const material of report.materials) {
      const label = material.direction === 'PRODUCED' ? 'получено' : 'переработано';
      parts.push(`${label} ${material.materialType.name}: ${formatKg(Number(material.qtyG))} кг`);
    }
    if (report.crewWork) {
      parts.push(
        `палет: запалечено ${report.crewWork.palletsPacked}, отгружено ${report.crewWork.palletsLoaded}`,
      );
    }
    return parts.join(' · ') || 'ничего не введено';
  };

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Приёмка смен</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      {error && <p className="mb-4 rounded-xl bg-rose-50 text-rose-800 ring-1 ring-rose-200 px-4 py-3 text-base">{error}</p>}
      {notice && (
        <p className="mb-4 rounded-xl bg-muted px-4 py-3 text-base text-body">{notice}</p>
      )}

      <h2 className="mb-3 text-lg font-semibold">Ждут подтверждения</h2>
      <div className="mb-8 flex flex-col gap-3">
        {reports !== null && pending.length === 0 && (
          <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">
            Непринятых смен нет.
          </p>
        )}
        {pending.map((report) => (
          <article key={report.id} className="rounded-xl bg-card shadow-card p-4">
            <p className="text-lg font-semibold">
              {report.machine.name} · смена {report.shiftNumber}
            </p>
            <p className="text-base text-dim">
              {report.site.name} · {formatDateRu(report.date)} ·{' '}
              {report.participants.map((item) => item.user.fullName).join(', ')}
            </p>
            <p className="mt-2 text-base text-body">{summary(report)}</p>
            {report.comment && (
              <p className="mt-1 text-base text-dim">Комментарий: {report.comment}</p>
            )}

            {/* Мастеру важно видеть, что снимки на месте: без них разбирать
                спор о качестве смены будет нечем */}
            <p className="mt-2 text-base text-dim">
              {report.photos.length === 0
                ? 'Снимков нет'
                : report.photos.map((photo) => PHOTO_KIND_NAMES[photo.kind]).join(', ')}
              {report.photoStatus === 'PENDING' && ' · загружаются'}
            </p>

            <button
              type="button"
              disabled={busy === report.id}
              onClick={() => void accept(report)}
              className="mt-3 w-full rounded-xl bg-emerald-600 px-5 py-4 text-lg font-semibold text-white disabled:opacity-50"
            >
              {busy === report.id ? 'Принимаю…' : 'Принять смену'}
            </button>
          </article>
        ))}
      </div>

      <h2 className="mb-3 text-lg font-semibold">Принятые</h2>
      <div className="flex flex-col gap-3">
        {accepted.length === 0 && (
          <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">Пока пусто.</p>
        )}
        {accepted.slice(0, 20).map((report) => (
          <article key={report.id} className="rounded-xl bg-canvas p-4">
            <p className="text-base font-semibold text-body">
              {report.machine.name} · {formatDateRu(report.date)} · смена {report.shiftNumber}
            </p>
            <p className="text-base text-faint">{summary(report)}</p>
          </article>
        ))}
      </div>
    </div>
  );
}
